import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { fetchCartsByCustomerId } from "../../shared/customerMastersApi";
import { IconList, IconRefresh } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

export function CustomerCartsPage() {
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get("customer");
  const customerId = Number(customerParam);

  const [customerMeta, setCustomerMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);

  const customerValid = Boolean(customerParam) && Number.isFinite(customerId) && customerId > 0;

  const reload = useCallback(async () => {
    if (!customerValid) return;
    setListLoading(true);
    const res = await fetchCartsByCustomerId(customerId);
    setListLoading(false);
    if (!res.ok) {
      setError(res.error ?? "Failed to load cart items");
      setItems([]);
      return;
    }
    setError(null);
    setItems(res.items);
  }, [customerId, customerValid]);

  useEffect(() => {
    if (!customerValid) return;
    let cancelled = false;
    (async () => {
      const [cRes, pRes] = await Promise.all([apiGet(`/api/Customers/${customerId}`), apiGet("/api/Products")]);
      if (cancelled) return;
      if (cRes.ok) setCustomerMeta(cRes.data);
      if (pRes.ok) setProducts(pRes.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, customerValid]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const customerLabel = customerMeta?.name ?? `Customer #${customerId}`;
  const productById = useMemo(() => {
    const map = new Map();
    for (const product of products) map.set(product.id, product);
    return map;
  }, [products]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        productName: productById.get(item.productId)?.name ?? item.productId,
        price: formatMoney(item.price),
        date: formatDateTime(item.date),
        active: item.isActive ? "Yes" : "No"
      })),
    [items, productById]
  );

  const columns = useMemo(
    () => [
      { field: "productName", headerName: "Product", flex: 1, minWidth: 150 },
      { field: "price", headerName: "Price", width: 120 },
      { field: "date", headerName: "Date", flex: 1, minWidth: 170 },
      { field: "active", headerName: "Active", width: 100 }
    ],
    []
  );

  if (!customerValid) {
    return <Navigate to="/customers-list" replace />;
  }

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Cart</h2>
          <p>{customerLabel}</p>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void reload()}
            disabled={listLoading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/customers-list" className="icon-button icon-button--toolbar" title="Customers list" aria-label="Back to customers list">
            <IconList />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={listLoading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
